import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, LogOut, Plus, Trash } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFAQs = () => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ question: "", answer: "", category: "General" });

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) navigate("/admin/login");
    else fetchFAQs();
  }, [navigate]);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API}/faqs`);
      setFaqs(response.data);
    } catch (error) {
      toast.error("Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    try {
      await axios.post(`${API}/admin/faqs`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("FAQ added successfully!");
      setDialogOpen(false);
      setFormData({ question: "", answer: "", category: "General" });
      fetchFAQs();
    } catch (error) {
      toast.error("Failed to add FAQ");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    const token = localStorage.getItem("adminToken");
    try {
      await axios.delete(`${API}/admin/faqs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("FAQ deleted!");
      fetchFAQs();
    } catch (error) {
      toast.error("Failed to delete FAQ");
    }
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="bg-primary text-primary-foreground py-6">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/admin"><ArrowLeft className="w-6 h-6 mr-6" /></Link>
            <span className="text-2xl font-heading font-bold">MURRAY</span>
            <span className="text-2xl font-heading font-light text-accent ml-2">REALTOR</span>
            <span className="ml-4 text-sm uppercase tracking-widest">Manage FAQs</span>
          </div>
          <Button onClick={() => { localStorage.removeItem("adminToken"); navigate("/admin/login"); }} variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary h-10 px-6 rounded-none">
            <LogOut className="w-4 h-4 mr-2" />Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-primary">FAQs</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 rounded-none"><Plus className="w-4 h-4 mr-2" />Add FAQ</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add New FAQ</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><Label>Category *</Label>
                  <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full h-10 px-3 border border-border rounded">
                    <option>General</option>
                    <option>Buying</option>
                    <option>Selling</option>
                  </select>
                </div>
                <div><Label>Question *</Label><Input required value={formData.question} onChange={(e) => setFormData({...formData, question: e.target.value})} /></div>
                <div><Label>Answer *</Label><Textarea required rows={4} value={formData.answer} onChange={(e) => setFormData({...formData, answer: e.target.value})} /></div>
                <Button type="submit" className="w-full bg-primary">Add FAQ</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? <div className="text-center py-20"><p>Loading...</p></div> : (
          <div className="space-y-4">
            {faqs.map((faq) => (
              <Card key={faq.id}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs uppercase tracking-widest text-accent font-semibold">{faq.category}</span>
                    <Button onClick={() => handleDelete(faq.id)} variant="ghost" size="sm" className="text-red-600"><Trash className="w-4 h-4" /></Button>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-primary mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFAQs;
